import { useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Plus,
  Search,
  RefreshCw,
  MoreHorizontal,
  Trash2,
  Package,
  User,
  Calendar,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/app/components/ui/pagination';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/app/components/ui/alert-dialog';
import { useKits, useDeleteKit } from '@/hooks/use-kits';
import { useOrgToday, useUsers } from '@/hooks/use-settings';
import { usePermissions } from '@/hooks/use-permissions';
import { formatDateOnly } from '@/lib/date-utils';
import type { Kit, KitStatus } from '@/lib/types';

const statusColors: Record<KitStatus, string> = {
  available: 'bg-green-100 text-green-800',
  deployed: 'bg-blue-100 text-blue-800',
  incomplete: 'bg-orange-100 text-orange-800',
};

const PAGE_SIZE = 20;

export function KitsPage() {
  const orgToday = useOrgToday();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [kitToDelete, setKitToDelete] = useState<Kit | null>(null);

  const page = parseInt(searchParams.get('page') || '1', 10);
  const status = (searchParams.get('status') || '') as KitStatus | '';
  const assignedToTech = searchParams.get('assigned_to_tech') || '';
  const q = searchParams.get('q') || '';

  const { data, isLoading, isError, refetch } = useKits({
    page,
    page_size: PAGE_SIZE,
    q,
    status,
    assigned_to_tech: assignedToTech ? parseInt(assignedToTech, 10) : '',
  });

  const { data: usersData } = useUsers({});
  const deleteMutation = useDeleteKit();
  const { canCreateAssets } = usePermissions();

  const kits = data?.results || [];
  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const users = usersData?.results || [];

  const updateSearchParams = useCallback(
    (updates: Record<string, string>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      if (!updates.page) {
        newParams.set('page', '1');
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const handleSearch = useCallback(() => {
    updateSearchParams({ q: searchInput, page: '1' });
  }, [searchInput, updateSearchParams]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch]
  );

  const handleDeleteClick = (kit: Kit) => {
    setKitToDelete(kit);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (kitToDelete) {
      await deleteMutation.mutateAsync(kitToDelete.id);
      setDeleteDialogOpen(false);
      setKitToDelete(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Kits</h1>
          <p className="text-gray-600 mt-1">
            Manage equipment kits for deployment
            {totalCount > 0 && ` (${totalCount} total)`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {canCreateAssets && (
            <Button asChild>
              <Link to="/kits/new">
                <Plus className="h-4 w-4 mr-2" />
                Create Kit
              </Link>
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search by kit name..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className="pl-10"
              />
            </div>
            <Select
              value={status}
              onValueChange={(value) => updateSearchParams({ status: value === 'all' ? '' : value })}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="deployed">Deployed</SelectItem>
                <SelectItem value="incomplete">Incomplete</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={assignedToTech}
              onValueChange={(value) => updateSearchParams({ assigned_to_tech: value === 'all' ? '' : value })}
            >
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="All Technicians" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Technicians</SelectItem>
                {users.map((user) => (
                  <SelectItem key={user.id} value={String(user.id)}>
                    {user.username}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleSearch}>Search</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="p-6 text-center text-red-600">
              Failed to load kits. Please try again.
            </div>
          ) : kits.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">No kits found.</p>
              {canCreateAssets && (
                <Button asChild className="mt-4">
                  <Link to="/kits/new">Create your first kit</Link>
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Kit Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Assets</TableHead>
                  <TableHead>Assigned Tech</TableHead>
                  <TableHead>Next Inspection</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {kits.map((kit) => (
                  <KitRow
                    key={kit.id}
                    kit={kit}
                    onDelete={() => handleDeleteClick(kit)}
                    orgToday={orgToday}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => updateSearchParams({ page: String(Math.max(1, page - 1)) })}
                className={page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              return (
                <PaginationItem key={pageNum}>
                  <PaginationLink
                    onClick={() => updateSearchParams({ page: String(pageNum) })}
                    isActive={page === pageNum}
                    className="cursor-pointer"
                  >
                    {pageNum}
                  </PaginationLink>
                </PaginationItem>
              );
            })}
            <PaginationItem>
              <PaginationNext
                onClick={() => updateSearchParams({ page: String(Math.min(totalPages, page + 1)) })}
                className={page >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Kit</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{kitToDelete?.name}"? This action cannot be undone.
              The assets in this kit will not be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Kit'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface KitRowProps {
  kit: Kit;
  onDelete: () => void;
  orgToday: string;
}

function KitRow({ kit, onDelete, orgToday }: KitRowProps) {
  const isOverdue =
    kit.next_inspection_due && kit.next_inspection_due.slice(0, 10) < orgToday;
  const { canEditAssets, canDeleteAssets } = usePermissions();

  return (
    <TableRow>
      <TableCell>
        <Link
          to={`/kits/${kit.id}`}
          className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
        >
          {kit.name}
        </Link>
        {kit.description && (
          <p className="text-sm text-gray-500 truncate max-w-xs">{kit.description}</p>
        )}
      </TableCell>
      <TableCell>
        <Badge className={statusColors[kit.status]}>{kit.status}</Badge>
        {!kit.is_complete && kit.status !== 'incomplete' && (
          <Badge variant="outline" className="ml-2 text-orange-600 border-orange-300">
            Missing items
          </Badge>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Package className="h-4 w-4 text-gray-400" />
          <span>{kit.asset_count}</span>
        </div>
      </TableCell>
      <TableCell>
        {kit.assigned_to_tech_username ? (
          <div className="flex items-center gap-1">
            <User className="h-4 w-4 text-gray-400" />
            <span>{kit.assigned_to_tech_username}</span>
          </div>
        ) : (
          <span className="text-gray-400">Unassigned</span>
        )}
      </TableCell>
      <TableCell>
        {kit.next_inspection_due ? (
          <div className={`flex items-center gap-1 ${isOverdue ? 'text-red-600' : ''}`}>
            <Calendar className="h-4 w-4" />
            <span>{formatDateOnly(kit.next_inspection_due)}</span>
            {isOverdue && <Badge variant="destructive" className="ml-1">Overdue</Badge>}
          </div>
        ) : (
          <span className="text-gray-400">Not set</span>
        )}
      </TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to={`/kits/${kit.id}`}>View Details</Link>
            </DropdownMenuItem>
            {canEditAssets && (
              <DropdownMenuItem asChild>
                <Link to={`/kits/${kit.id}/edit`}>Edit Kit</Link>
              </DropdownMenuItem>
            )}
            {canDeleteAssets && (
              <DropdownMenuItem className="text-red-600" onClick={onDelete}>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Kit
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}
